import { BaseViewStore, BaasicDropdownStore } from "core/stores";
import { FacilityOrderTemplateItemService, FacilityOrderService, FacilityOrderTemplateService, FacilityOrderStatusService } from "application/facility-order/services";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { FacilityOrderPatientService } from "application/facility-order-new-flow-insurance/services";
import { LocalPatientService } from "application/patient/services";
import { ScheduleService } from 'application/schedule/services';
import { action, computed, observable } from "mobx";
import { QueryUtility } from 'core/utils';
import _, { debounce } from 'lodash';
import { LookupService } from "common/services";
import { ModalParams } from 'core/models';

class FacilityOrderNewFlowOrderSummaryViewStore extends BaseViewStore {
	pickTicketErrorMessage = "There are no items to include on the Pick Ticket.";
	agoraPoErrorMessage = "There are no items to include on the Agora PO.";
	importErrorMessage = "There are no templates to import to BT.";

	@observable accessor facilityOrderTemplateItemsGroupedByItemCategory = [];
	@observable accessor facilityOrderTemplateItemsGroupedByItemCategoryEditMode = [];
	@observable accessor isStatusBarVisible = true;
	@observable accessor fetchingData = false;
	@observable accessor signalRFetch = false;
	@observable accessor isEditDisabled = false;
	@observable accessor shouldShowItemActionButtons = false;
	@observable accessor savingResource = false;
	@observable accessor rebuildTooltip = false;
	@observable accessor facilityOrder;
	@observable accessor pickTicketHeaderStatus;
	@observable accessor pickTicketHeader = null;
	@observable accessor isImportButtonVisible = false;
	@observable accessor anyDNS = false;
	@observable accessor showAllDNS = false;
	@observable accessor item = null;
	@observable accessor sendOrderLoading = false;
	@observable accessor sendOrderAll = false;
	@observable accessor isImportDisabled = false;
	@observable accessor facilityOrderStatus;
	@observable accessor isNonScheduledItemsButtonVisible = false;
	@observable accessor disabledSendOrder = false;
	@observable accessor disabledItemButtons = false;
	@observable accessor clinicalReviewStatus;
	@computed
	get isOrderSummaryStarted() {
		return this.facilityOrder && this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'order-summary') !== undefined
	}
	@observable accessor patientFilterValue = null;
	@observable accessor allItemDetailsExpanded = false;
	@observable accessor hasBackorderedItem = false;
	@observable accessor isSendOrderChecking = false;
	ssQtyInputRef = '';

	constructor(rootStore) {
        super(rootStore);

        this.rootStore = rootStore;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.facilityOrderId = rootStore.routerStore.routerState.params.id;
        this.facilityOrderTemplateItemService = new FacilityOrderTemplateItemService(rootStore.application.baasic.apiClient);
        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.localPatientService = new LocalPatientService(rootStore.application.baasic.apiClient);
        this.facilityOrderPatientService = new FacilityOrderPatientService(rootStore.application.baasic.apiClient);
        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
			await this.fetchFacilityOrderTemplateItems(filter);
		}, {});

        this.fetch([this.initialize()]);
        this.fetchDebouncedData = debounce(this.fetchData, 500);

        this.setAnyDNS = debounce(() => {
			this.anyDNS = false;
		}, 500);

        this.localPatientStore = new BaasicDropdownStore({
			filterable: true,
			textField: "name",
			initFetch: false,
		}, {
			fetchFunc: async () => {
				const facilityOrderPatients = (await this.facilityOrderPatientService.find({ facilityOrderId: this.facilityOrderId, embed: "Patient", fields: "patientId,patient,activeStatus,isNewPatient", rpp: 1000, sort: "patient|asc" })).data.item;
				const finalPatients = facilityOrderPatients.filter(fop => {

					if (fop.activeStatus !== "" && fop.activeStatus !== "Active" && fop.activeStatus !== "Hospital") {
						return false;
					}

					if (fop.activeStatus === "Active") {
						return this.originalData && this.originalData.some(item =>
							item.facilityOrderTemplateItemOrderSummaryDetails &&
							item.facilityOrderTemplateItemOrderSummaryDetails.some(detail =>
								detail.patientId === fop.patientId &&
								(
									detail.inactiveReasonId == null ||
									detail.inactiveReasonId === 0 ||
									detail.inactiveReasonId === 16
								)
							)
						);
					}

					return true;
				});

				return finalPatients.map(fop => {
					return {
						name: `${fop.patient.lastName}${(!fop.patient.firstName || fop.patient.firstName === "" ? "" : ",")} ${fop.patient.firstName}`,
						tag: fop.activeStatus === "Hospital" ? "Hospital" : undefined,
						disabled: fop.activeStatus === "Hospital",
						icon: true,
						...fop
					};
				});
			}
		});

        this.baasicAcknowledgeErrorsModal = new ModalParams({ onClose: null });
        this.baasicAcknowledgeErrorsModal.onCancel = () => { this.baasicAcknowledgeErrorsModal.close() };

        this.cutbackConfirmModal = new ModalParams({ onClose: null });
        this.cutbackConfirmModal.onCancel = () => { this.cutbackConfirmModal.close(); };
        this.cutbackConfirmModal.yesLabel = 'Yes';
        this.cutbackConfirmModal.noLabel = 'Cancel';
        this.cutbackConfirmModal.swapButtons = true;

        this.importConfirmModal = new ModalParams({ onClose: null });
        this.importConfirmModal.onConfirm = async () => { this.importConfirmModal.close(); await this.startImportFlow(); };
        this.importConfirmModal.onCancel = () => { this.importConfirmModal.close() };
        this.importConfirmModal.yesLabel = 'Yes';
        this.importConfirmModal.noLabel = 'No';

        this.sendOrderConfirmModal = new ModalParams({ onClose: null });
        this.sendOrderConfirmModal.onConfirm = async () => { this.sendOrderConfirmModal.close(); await this.sendOrderAndImport(); };
        this.sendOrderConfirmModal.onCancel = () => { this.sendOrderConfirmModal.close() };
        this.sendOrderConfirmModal.yesLabel = 'Yes';
        this.sendOrderConfirmModal.noLabel = 'No';

        this.facilityOrderNewFlowUSIntakeFindAndReplaceModal = new ModalParams({});
        this.facilityOrderNewFlowExtraSuppliesAddModal = new ModalParams({ onClose: () => { this.hasBackorderedItems(); } });
        this.facilityOrderNewFlowNonScheduledTubesModal = new ModalParams({});
        this.facilityOrderNewFlowRoundingItemsModal = new ModalParams({
			onClose: () => {
				this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
					category.items.forEach(item => {
						if (item.showRoundOption && item.patientCount < 4) {
							item.includeItem = false;
						}
					});
				});
			}
		});

        this.primaryRecipientErrorModal = new ModalParams({ onClose: null });
        this.primaryRecipientErrorModal.onCancel = () => { this.primaryRecipientErrorModal.close() };
        this.primaryRecipientErrorModal.facilityOrderId = this.facilityOrderId;

        const userRoles = this.rootStore.userStore.user.roles;
        this.isAdmin = userRoles.includes("Administrators");
        this.isImportButtonVisible = userRoles.includes("Administrators") || userRoles.includes("Biller");
    }

	@action.bound
	async initialize() {
		const lookupService = new LookupService(this.rootStore.application.baasic.apiClient);
		this.lookups = await lookupService.getLookups("ItemCategory,ImportHeaderStatus");
		this.enteralItemCategoryId = _.find(this.lookups.itemCategory, s => { return s.abrv === 'enteral'; }).id;
		await this.initializeFacilityOrder();
		await this.initializePickTicketHeader();
		await this.hasBackorderedItems();
		this.importHeaderStatus = this.lookups.importHeaderStatus;
		this.queryUtility.fetch();
		this.patientName = '';
		this.itemName = '';
		this.rootStore.titleStore.setTitle(`Order Summary - ${this.facilityOrder.facility.name}`);

		const typeResponse = await this.scheduleService.getTypes();
		this.orderType = _.find(typeResponse.item, { abrv: 'ordering' });
	}

	@action.bound
	async setItemValue(value) {
		this.item = value;
		this.itemName = value ? value.itemName : '';
		await this.filterOrderSummaryDetails(value ? value.itemName : '');
	}

	@action.bound
	async hasBackorderedItems() {
		this.hasBackorderedItem = (await this.facilityOrderTemplateService.hasBackorderedItem({ facilityOrderId: this.facilityOrderId })).data;
	}

	@action.bound
	async checkIsNonScheduledItemsButtonVisible() {
		const hideGTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "enteral" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
		const hideTrachTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "respiratory" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
		if (hideGTube && hideTrachTube) {
			return false;
		}

		let params = {
			pageSize: 1000,
			facilityOrderId: this.facilityOrder.id,
			notCurrentTube: true,
			embed: 'FacilityOrderTemplateItems,SubCategory',
			orderBy: "SubCategory",
			orderDirection: "asc",
			inActive: false,
			itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
		}

		const response = await this.facilityOrderTemplateService.find(params);
		return response.data.item && response.data.item.some(i => i.facilityOrderTemplateItems.some(x => x.importAction.abrv != 'delete' && x.orderQuantity) && ((i.subCategory.abrv == "G-Tube" && !hideGTube) || (i.subCategory.abrv == "Trach-Tube" && !hideTrachTube)));
	}

	@action.bound
	async initializeFacilityOrder() {
		const response = await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses" });
		this.facilityOrder = response.data;
		this.isInventoryStepAllowed = (await this.facilityOrderService.isInventoryStepAllowed(this.facilityOrderId)).data;
		if (this.facilityOrder && this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares) {
			this.showRoundOption = !(this.facilityOrder.facility.facilityAreaOfCares.some(faoc => (faoc.areaOfCare && faoc.areaOfCare.abrv === "enteral") && (faoc.coverageType && faoc.coverageType.abrv === "coveredOnly")));
			this.showFacilityStock = this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.coverageType && faoc.coverageType.abrv === "all");
		}
		this.isNonScheduledItemsButtonVisible = await this.checkIsNonScheduledItemsButtonVisible();
		this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'send-order');
		this.disabledSendOrder = !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder;
		this.clinicalReviewStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "clinical-review");
	}

	@action.bound
	async initializePickTicketHeader() {
		if (!this.facilityOrder.pickTicketHeaderId) {
			this.facilityOrder.pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrderId, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
		}

		if (this.facilityOrder.pickTicketHeaderId) {
			const pickTicketHeader = (await this.pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
			this.pickTicketHeader = pickTicketHeader;
			this.pickTicketHeaderStatus = pickTicketHeader && pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv;
		}
		this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
		this.shouldShowItemActionButtons = !this.isEditDisabled
	}

	@action.bound
	async fetchFacilityOrderTemplateItems(filter) {
		this.fetchingData = !this.signalRFetch ? true : false;
		this.signalRFetch = false;

		let params = {
			...filter,
			pageSize: 10000,
			facilityOrderId: this.facilityOrderId,
		}
		if (this.facilityOrder.isEnteralWeek) {
			params = _.extend(params, {
				itemCategoryId: this.enteralItemCategoryId
			});
		}
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];
		const response = await this.facilityOrderTemplateItemService.getOrderSummaryItems(params);
		this.originalData = response && response.data && response.data.item && response.data.item;
		if (this.originalData) {
			let grouped = _.groupBy(this.originalData, 'itemCategory');
			_.each(grouped, (val, key) => {
				facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
					id: key,
					category: val[0].itemCategory,
					items: _.map(val, (i) => {
						if (i.facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId == 16 || this.isNonScheduled(x))) {
							this.anyDNS = true;
						}
						i.originalSendSeparatelyQuantitySum = i.sendSeparatelyQuantitySum;
						return {
							...i,
							showDNSItems: false,
							show: false,
							hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId == 16),
							isEditMode: false,
							isEditSendSeparatelyMode: false,
							showNonScheduledItems: false,
							isRefreshed: false,
							hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
							showRoundOption: val[0].itemCategory.toLowerCase() == "enteral" && i.hasCaseVersion && i.qtyPerCase != 0 && i.procedureCode != "B4153" && !i.orderQuantityUnitOfMeasures.includes(", 0 EA") && i.orderQuantitySum != 0,
							includeItem: false
						};
					}),
					show: true
				});
			});

			this.filterItemStore();
			await this.localPatientStore.filterAsync();
			this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp;
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = _.cloneDeep(facilityOrderTemplateItemsGroupedByItemCategoryTemp);
			this.fetchingData = false;

			if (this.patientName) {
				this.onPatientChange(this.patientName)
			}
			if (this.itemName) {
				this.onItemChange(this.itemName)
			}
		}
	}

	@action.bound
	changeStatusBarVisibility() {
		this.isStatusBarVisible = !this.isStatusBarVisible;
	}

	@action.bound
	isNonScheduled(x) {
		return (x.notCurrentTube && (x.inactiveReasonId == null || x.inactiveReasonId == 0) && x.orderQuantity > 0);
	}

	@action.bound
	refreshEditModeItems(index, index2) {
		if (index != null && index2 != null) {
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails
				.forEach(item => {
					item.orderQuantity = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity;
					item.sendSeparatelyQty = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).sendSeparatelyQty;
				});

			this.refreshErrorMessage(this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]);
		}
	}

	@action.bound
	async refreshErrorMessage(item) {
		if (item) {
			let params = {
				pageSize: 1,
				facilityOrderId: this.facilityOrderId,
				productId: item.productId
			}

			if (this.facilityOrder.isEnteralWeek) {
				params = _.extend(params, {
					itemCategoryId: this.enteralItemCategoryId
				});
			}

			const response = await this.facilityOrderTemplateItemService.getOrderSummaryItems(params);
			item.errorMessage = response && response.data && response.data.item && response.data.item[0] ? response.data.item[0].errorMessage : null;
		}
	}

	@action.bound
	showFacilityOrderTemplateItemOrderSummary(index) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].show = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].show;
	}

	@action.bound
	showFacilityOrderTemplateItemOrderSummaryDetails(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show;
		if (!this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].show) {
			this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].isRefreshed = false;
		}
	}

	@action.bound
	expandAllItemDetails() {
		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.show = true;
			category.items.forEach(item => {
				item.show = true;
			});
		});
	}

	@action.bound
	collapseAllItemDetails() {
		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.show = false;
			});
		});
	}

	@action.bound
	showDNSItems(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showDNSItems;
	}
	@action.bound
	showNonScheduledItems(index, index2) {
		this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems = !this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].showNonScheduledItems;
	}

	@action.bound
	showAllDNSItems() {
		this.showAllDNS = !this.showAllDNS;

		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.showDNSItems = this.showAllDNS;
				item.showNonScheduledItems = this.showAllDNS;
			});
		});
	}

	@action.bound
	async signalRAction(message) {
		if (!this.facilityOrder.sendOrder) {
			this.hasBackorderedItems();
		}
		if (message.table == "Product") {
			return;
		}
		if (message.table == "SendOrder") {
			if (message.column == "SendOrder") {
				this.rootStore.notificationStore.success("Facility Schedule Date successfully updated.");
			} else if (message.column == "PickTicketEmail") {
				this.rootStore.notificationStore.success("Pick Ticket email successfully sent.");
			} else if (message.column == "AgoraPO") {
				this.rootStore.notificationStore.success("Agora PO successfully sent.");
			} else if (message.column == "Import") {
				this.rootStore.notificationStore.success("Importing template(s) to Brightree is in progress.");
			}
			return;
		}
		else if (message.table == "FO") {
			if (message.column == "SendOrder") {
				this.facilityOrder.sendOrder = true;
				this.disabledSendOrder = !this.isAdmin;
				this.isEditDisabled = true;
				this.shouldShowItemActionButtons = !this.isEditDisabled
			}
			else {
				await this.getDataAfterImport();
			}
			return;
		}
		else if (message.table == "PTH") {
			return await this.initializePickTicketHeader();
		}
		else if (message.table == "FOS") {
			return await this.getFacilityOrderStatus();
		}

		await this.refreshData(message);

		if (this.patientFilterValue)
			this.onPatientChange(this.patientFilterValue);
	}

	@action.bound
	async getDataAfterImport() {
		await this.initializeFacilityOrder();
		await this.initializePickTicketHeader();
		await this.refreshData();
	}

	@action.bound
	async getFacilityOrderStatus() {
		const facilityOrderStatusService = new FacilityOrderStatusService(this.rootStore.application.baasic.apiClient);
		var statusesResponse = await facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
		this.facilityOrder.facilityOrderStatuses = statusesResponse.data ? [...statusesResponse.data.item] : [];
		this.changeStatusBarVisibility();
		this.changeStatusBarVisibility();
	}

	async fetchData() {
		await this.initialize();
		this.rootStore.notificationStore.warning("This page has been refreshed due to changes made on another page");
	}

	@action.bound
	onPatientChange(value) {
		this.patientFilterValue = value;
		this.showAllDNS = !!value;

		this.patientName = value;

		this.filterOrderSummaryDetails(value);

		this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
			category.items.forEach(item => {
				item.showDNSItems = this.showAllDNS;
				item.showNonScheduledItems = this.showAllDNS;
			});
		});
	}

	@action.bound
	onItemChange(value) {
		this.itemName = value;
		this.filterOrderSummaryDetails(value);
	}

	@action.bound
	async filterOrderSummaryDetails(value) {
		const patient = this.localPatientStore.items.find(x => x.name === this.patientName);
		const item = this.itemStore.find(x => x.itemId === this.itemName || x.itemName === this.itemName);
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];
		if (this.originalData) {
			let filteredItems = this.originalData.filter(i =>
				i.facilityOrderTemplateItemOrderSummaryDetails.some(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))) &&
				(item ? i.itemId === item.itemId : (i.itemId.toLowerCase().includes(this.itemName.toLowerCase()) || i.itemName.toLowerCase().includes(this.itemName.toLowerCase())))
			);

			let grouped = _.groupBy(filteredItems, 'itemCategory');
			_.each(grouped, (val, key) => {
				facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
					id: key,
					category: val[0].itemCategory,
					items: _.map(val, (i) => {
						return {
							...i,
							facilityOrderTemplateItemOrderSummaryDetails: i.facilityOrderTemplateItemOrderSummaryDetails.filter(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))),
							showDNSItems: patient || item ? true : false,
							show: value == "" && !patient && !item ? false : true,
							hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId == 16),
							isEditMode: false,
							isEditSendSeparatelyMode: false,
							showNonScheduledItems: patient || item ? true : false,
							hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
							showRoundOption: val[0].itemCategory.toLowerCase() == "enteral" && i.hasCaseVersion && i.qtyPerCase != 0 && i.procedureCode != "B4153" && !i.orderQuantityUnitOfMeasures.includes("0 EA") && i.orderQuantitySum != 0,
						};
					}),
					show: true
				});
			});
		}
		this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = _.cloneDeep(facilityOrderTemplateItemsGroupedByItemCategoryTemp);

		this.rebuildTooltip = true;
		this.rebuildTooltip = false;

	}

	@action.bound
	async cutbackSalesOrderQuantity(option, group, index, index2) {
		if (option == "cutback-all" && group.defaultItemTypeAbrv == "spike-set" && group.facilityOrderTemplateItemOrderSummaryDetails.some(x => (x.clinicalStatus == "Covered" || x.clinicalStatus == "Prior Auth Obtained") && x.orderQuantity > 0 && x.inactiveReasonId != 16)) {
			this.rootStore.notificationStore.error("Unable to cutback on all for spike sets. (Cutting back on covered spike sets may cause billing issues. to adjust, use “Cut Back on Specific” instead.)");
			return;
		}

		if (option == "cutback-noncovered") {
			await this.cutbackSOQuantities(option, group);
			this.refreshEditModeItems(index, index2);
		}
		else if (option == "cutback-all") {
			this.cutbackConfirmModal.onConfirm = async () => { await this.cutbackSOQuantities(option, group, index); this.cutbackConfirmModal.close(); };
			this.cutbackConfirmModal.open({
				message: "Are you sure you want to cutback on the Covered patients?"
			});
		}
		else if (option == "cutback-specific") {
			group.isEditMode = true;
			group.isEditSendSeparatelyMode = false;
		}
	}

	@action.bound
	async cutbackSOQuantities(option, group, index, index2) {
		this.savingResource = true;
		if (index != null && index2 != null &&
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => {
				const original = this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(
					i => i.facilityOrderTemplateItemId === x.facilityOrderTemplateItemId
				);
				return (group.defaultItemTypeAbrv === "spike-set") && (x.clinicalStatus === "Covered" || x.clinicalStatus === "Prior Auth Obtained") && original && x.orderQuantity != original.orderQuantity;
			})
		) {
			this.rootStore.notificationStore.warning("When cutting back on covered spike sets, the syringes cannot be covered. Please adjust accordingly.");
		}

		let facilityOrderTemplateIds = null;
		if (index != null && index2 != null) {
			facilityOrderTemplateIds = this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.map(x => ({ id: x.facilityOrderTemplateItemId, orderQuantity: x.orderQuantity }));
		}
		else {
			facilityOrderTemplateIds = group.facilityOrderTemplateItemOrderSummaryDetails.filter(x => (!x.inactiveReasonId || x.inactiveReasonId == 0) && (option == "cutback-noncovered" ? !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Covered" || x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" ||  x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) : true) && (group.defaultItemTypeAbrv != "spike-set" || (group.defaultItemTypeAbrv == "spike-set" && (x.clinicalStatus != "Covered" && x.clinicalStatus != "Prior Outh Obtained")))).map(x => ({ id: x.facilityOrderTemplateItemId, orderQuantity: null }));
		}

		if (facilityOrderTemplateIds.some(x => x)) {
			let result = await this.facilityOrderTemplateItemService.cutbackSalesOrderQuantities(facilityOrderTemplateIds);
			if (result.statusCode == 201 && result.data && result.data.length > 0) {
				let isUpdated = false;
				result.data.forEach(item => {
					let matchingItem = group.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
					if (matchingItem) {
						if (!isUpdated)
							isUpdated = matchingItem.orderQuantity != item.orderQuantity;
						matchingItem.orderQuantity = !item.orderQuantity ? 0 : item.orderQuantity;
						if (index != null && index2 != null) {
							this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id).orderQuantity = item.orderQuantity;
						}
						else if (index != null) {
							this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items.forEach(i => {
								let matchingItem = i.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
								if (matchingItem) {
									if (!isUpdated)
										isUpdated = matchingItem.orderQuantity != item.orderQuantity;
									matchingItem.orderQuantity = item.orderQuantity
								}
							});
						}
					}
				});
				this.recalculateOrderQuantity(group);
				if (index != null && index2 != null) {
					this.refreshErrorMessage(this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2]);
				}
				this.rebuildTooltip = true;
				this.rebuildTooltip = false;
				if (isUpdated)
					this.rootStore.notificationStore.success("SO quantities successfully updated.", 2000);
				group.originalSendSeparatelyQuantitySum = group.sendSeparatelyQuantitySum;
				this.rebuildTooltip = true;
				this.rebuildTooltip = false;
				this.syncQuantityToOriginalData(result.data, "orderQuantity");
			} else {
				this.rootStore.notificationStore.error("An error occured while updating SO quantities.");
			}
		}
		else {
			this.rootStore.notificationStore.warning("No items found for update.");
		}
		this.hasBackorderedItems();
		group.isEditMode = false;
		this.savingResource = false;
	}

	@action.bound
	async cutbackSendSeparatelyQuantities(group, index, index2, inputValue) {
		this.savingResource = true;

		const ssQty = parseInt(inputValue);

		const items = this
			.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index]
			.items[index2]
			.facilityOrderTemplateItemOrderSummaryDetails
			.map(x => ({ id: x.facilityOrderTemplateItemId }));

		const result = await this.facilityOrderTemplateItemService
			.updateBatchSendSeparatelyQuantity({ ssQty, items });

		if (result.statusCode == 201) {
			let isUpdated = false;
			result.data.forEach(item => {
				let matchingItem = group.facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId === item.id);
				if (matchingItem) {
					if (!isUpdated) {
						isUpdated = matchingItem.sendSeparatelyQty != item.sendSeparatelyQty;
					}
					matchingItem.sendSeparatelyQty = item.sendSeparatelyQty;
				}
			});
			group.sendSeparatelyQuantitySum = _.sumBy(
				group.facilityOrderTemplateItemOrderSummaryDetails.filter(
					i => i.inactiveReasonId !== 16
				),
				i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
			);
			this.rebuildTooltip = true;
			this.rebuildTooltip = false;
			if (isUpdated)
				this.rootStore.notificationStore.success("SS QTY successfully added.", 2000);
			group.originalSendSeparatelyQuantitySum = ssQty;
			this.rebuildTooltip = true;
			this.rebuildTooltip = false;
			this.syncQuantityToOriginalData(result.data, "sendSeparatelyQty");
		} else {
			this.rootStore.notificationStore.error("An error occured while updating SO quantities.");
		}
		this.refreshEditModeItems(index, index2);
		group.isEditSendSeparatelyMode = false;
		this.savingResource = false;
	}

	@action.bound
	resetSSQuantitySum(group) {
		group.isDefaultUpdated = false;
		group.sendSeparatelyQuantitySum = _.sumBy(
			group.facilityOrderTemplateItemOrderSummaryDetails.filter(
				i => i.inactiveReasonId !== 16
			),
			i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
		);
	}

	@action.bound
	syncQuantityToOriginalData(updated, valueField) {
		if (!this.originalData || !this.originalData.length || !updated || !updated.length) return;

		const valueById = {};
		for (const item of updated) {
			valueById[item.id] = item[valueField] != null ? item[valueField] : 0;
		}

		for (const group of this.originalData) {
			const details = group.facilityOrderTemplateItemOrderSummaryDetails || [];
			let wasModified = false;

			for (const detail of details) {
				if (valueById.hasOwnProperty(detail.facilityOrderTemplateItemId)) {
					detail[valueField] = valueById[detail.facilityOrderTemplateItemId];
					wasModified = true;
				}
			}

			if (!wasModified) continue;

			if (valueField === "orderQuantity") {
				const activeScheduledItems = details.filter(item => {
					const isActive = !item.inactiveReasonId || item.inactiveReasonId === 0;
					const isScheduled = !(item.notCurrentTube && parseInt(item.orderQuantity, 10) > 0);
					return isActive && isScheduled;
				});

				const sum = _.sumBy(activeScheduledItems, item => parseInt(item.orderQuantity, 10) || 0);

				group.orderQuantitySum = sum;

				if (!group.qtyPerCase || sum === 0) {
					group.orderQuantityUnitOfMeasures = "";
				} else {
					const cs = Math.floor(sum / group.qtyPerCase);
					const ea = sum % group.qtyPerCase;
					group.orderQuantityUnitOfMeasures = `${cs} CS, ${ea} EA`;
				}
			} else if (valueField === "sendSeparatelyQty") {
				group.sendSeparatelyQuantitySum = _.sumBy(
					group.facilityOrderTemplateItemOrderSummaryDetails.filter(
						i => i.inactiveReasonId !== 16
					),
					i => parseInt(i.sendSeparatelyQty ? i.sendSeparatelyQty : 0)
				);
			}
		}
	}

	@action.bound
	editOrderQuantity(item, index, index2, value) {
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity = parseInt(value);
	}

	@action.bound
	saveOrderQuantity(item, index, index2, value) {
		let minQuantity = item.sendSeparatelyQty ? item.orderQuantity - (item.orderQuantity - item.sendSeparatelyQty) : 0;
		if (parseInt(value) < minQuantity) {
			this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.find(x => x.facilityOrderTemplateItemId == item.facilityOrderTemplateItemId).orderQuantity = "";
			this.rootStore.notificationStore.error("An SS has already been applied to this item. The sales order qty cannot be lower than the SS qty.");
		}
	}

	@action.bound
	editBatchSendSeparatelyQty(group, index, index2, value) {
		if (parseInt(value) < 0) {
			this.rootStore.notificationStore.error("SS QTY must be greater than 0.");
			return;
		}
		let groupTotalSO = group.orderQuantitySum ? parseInt(group.orderQuantitySum, 10) : 0;
		if (parseInt(value) > groupTotalSO) {
			this.rootStore.notificationStore.error("SS QTY mustn't be greater than SO QTY sum.");

			if (this.ssQtyInputRef) {
				this.ssQtyInputRef.value = "";
			}
			return;
		}
		group.sendSeparatelyQuantitySum = value;
		group.isDefaultUpdated = true;
	}

	@action.bound
	showItem(index, index2) {
		return this.facilityOrderTemplateItemsGroupedByItemCategory[index].items[index2].facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId != 16 && !this.isNonScheduled(x));
	}

	@action.bound
	orderHistory() {
		window.open(this.rootStore.routerStore.findRoute("master.app.main.facility-order.facility-order-new-flow.history").pattern.replace(":id", this.facilityOrder.id));
	}

	@action.bound
	async goToFacilityPreview() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
	}

	@action.bound
	async goToFacilityCalendar() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderType.id}`);
	}

	@action.bound
	async downloadFacilityOrder() {
		const downloadUrl = `${(await this.facilityOrderTemplateService.getDownloadNewFlowUrl({ ...this.queryUtility.plainFilter, facilityOrderId: this.facilityOrder.id }))}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
			}&ttype=${this.rootStore.authStore.token.type}`
		window.open(downloadUrl, "_blank")
	}

	@action.bound
	async openInsuranceCoverageStatusOverviewEmail() {
		window.open(`${this.rootStore.routerStore
			.findRoute('master.app.main.facility-order.facility-order-new-flow.overview')
			.pattern.replace(":facilityOrderId", this.facilityOrder.id)}?isNiko=false`);
	}

	@action.bound
	async sendOrderNewFlow() {
		try {
			this.sendOrderLoading = true;
			await this.scheduleService.sendOrderOrderSummaryNewFlow({ facilityOrderId: this.facilityOrderId });
			this.rootStore.notificationStore.success("Facility Schedule Date successfully updated.");
			await this.pickTicketHeaderService.sendInfoEmail(this.facilityOrderId);
			this.rootStore.notificationStore.success("Pick Ticket email successfully sent.");
		} catch (err) {
			err.data == this.pickTicketErrorMessage ? this.rootStore.notificationStore.success(err.data) : this.rootStore.notificationStore.error(err.data.message ? err.data.message : err.data);
		} finally {
			await this.initializeFacilityOrder();
			await this.initializePickTicketHeader();
			this.sendOrderLoading = false;
		}
	}

	@action.bound
	async sendOrderAndImport() {
		try {
			this.sendOrderAll = true;
			var result = await this.pickTicketHeaderService.sendOrderOrderSummary({ facilityOrderId: this.facilityOrderId });

			if (result.data.some(item => item == this.pickTicketErrorMessage)) {
				this.rootStore.notificationStore.success(this.pickTicketErrorMessage);
			}
			if (result.data.some(item => item == this.agoraPoErrorMessage)) {
				this.rootStore.notificationStore.success(this.agoraPoErrorMessage);
			}
			if (result.data.some(item => item == this.importErrorMessage)) {
				this.rootStore.notificationStore.success(this.importErrorMessage);
			}
			result.data = result.data.filter(item => item != this.pickTicketErrorMessage && item != this.agoraPoErrorMessage && item != this.importErrorMessage);
			if (result.data.length) {
				this.openAcknowledgeErrorsModal(result.data);
			}
		} catch (err) {
			this.rootStore.notificationStore.error("Error occurred while trying to send Order.", err);
		} finally {
			await this.initializeFacilityOrder();
			await this.initializePickTicketHeader();
			this.sendOrderAll = false;
		}
	}

	@action.bound
	openAcknowledgeErrorsModal(errors) {
		this.baasicAcknowledgeErrorsModal.open({
			errors: errors
		});
	}

	@action.bound
	openPrimaryRecipientErrorModal(processAction, missingName) {
		this.primaryRecipientErrorModal.open({
			processAction: processAction,
			missingName: missingName
		});
	}

	@action.bound
	async showImportConfirmModal(sendOrder) {
		if (sendOrder) {
			this.isSendOrderChecking = true;
			let res = (await this.facilityOrderTemplateService.importCheck(this.facilityOrder.id, 'Send Order')).data;
			if (!res.success) {
				this.isSendOrderChecking = false;
				return this.rootStore.notificationStore.error(res.errors);
			}
			const recipientData = await this.hasPrimaryRecipient();
			const hasPickTicketRecipient = recipientData.pickTicketRecipientType;
			const hasPrimaryDeliveryRecipient = recipientData.deliveryTicketType;

			if (!hasPickTicketRecipient || !hasPrimaryDeliveryRecipient) {
				const missingName = [!hasPickTicketRecipient && "Packing List", !hasPrimaryDeliveryRecipient && "Delivery Ticket"].filter(Boolean).join(" and ");
				this.openPrimaryRecipientErrorModal(this.showImportConfirmModalExtension, missingName);
				this.isSendOrderChecking = false;
				return;
			}

			this.isSendOrderChecking = false;
			await this.showImportConfirmModalExtension();
		}
		else {
			await this.importConfirmModal.open({
				message: 'FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_IMPORT'
			});
		}
	}

	@action.bound
	async showImportConfirmModalExtension() {
		let params = {
			pageSize: 1,
			facilityOrderId: this.facilityOrder.id,
			notCurrentTube: true,
			hasIncludeNotCurrentTube: true,
			fields: "id",
			itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
		}
		const response = await this.facilityOrderTemplateService.find(params);
		const isNotCurrentTubeSaved = response.data.item && response.data.item.some(i => i);
		const isNotCurrentTubeAdded = (await this.facilityOrderService.get(this.facilityOrderId, { fields: "IsNotCurrentTubeAdded" })).data.isNotCurrentTubeAdded;
		if (!isNotCurrentTubeAdded && this.isNonScheduledItemsButtonVisible && (this.facilityOrder.facility.isMonthlyTrachTube || isNotCurrentTubeSaved)) {
			return this.facilityOrderNewFlowNonScheduledTubesModal.open({ facilityOrderId: this.facilityOrder.id, sendOrder: async () => await this.sendOrderConfirmModal.open({ message: 'FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_SEND_ORDER' }), fetchData: () => this.queryUtility.fetch(), disabledSendOrder: this.disabledSendOrder });
		}
		await this.sendOrderConfirmModal.open({
			message: 'FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_SEND_ORDER'
		});
	}

	@action.bound
	async startImportFlow() {
		try {
			this.isImportDisabled = true;
			this.rootStore.notificationStore.success("Importing template(s) to Brightree is in progress.");
			await this.facilityOrderService.importFacilityOrderNewFlow({ facilityOrderId: this.facilityOrder.id });
		}
		catch (err) {
			err.data.errors == this.importErrorMessage ? this.rootStore.notificationStore.success(err.data.errors) : this.rootStore.notificationStore.error(err.data.errors);
		}
		this.isImportDisabled = false;
	}

	@action.bound
	async setPickTicketHeaderStatus() {
		try {
			const response = await this.pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "facilityOrders,pickTicketHeaderStatus" });
			this.pickTicketHeader = response.data;

			await this.initializeFacilityOrder();
		}
		catch (err) {
			this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
		}
	}

	@action.bound
	isNewPatientById(patientId) {
		const items = this.localPatientStore && this.localPatientStore.items || [];
		return items.some(p => p && p.patientId === patientId && p.isNewPatient);
	}

	@action.bound
	async openFacilityOrderNewFlowUSIntakeFindAndReplaceModal() {
		let errorMessage = null;
		if (this.disabled) {
			errorMessage = "This action is not allowed until the Clinical Review is started.";
		}
		else if (this.facilityOrder.pickTicketHeaderId) {
			const pickTicketHeader = (await this.pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "pickTicketHeaderStatus", fields: "pickTicketHeaderStatus" })).data;
			if (pickTicketHeader && pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv != "new") {
				errorMessage = pickTicketHeader.pickTicketHeaderStatus.abrv == "in-progress" ? "This action is not allowed because purchasing is in progress." : "This action is not allowed because purchasing is complete.";
			}
		}
		if (errorMessage) {
			this.rootStore.notificationStore.error(errorMessage);
			return;
		}

		this.facilityOrderNewFlowUSIntakeFindAndReplaceModal.open({
			facilityOrderId: this.facilityOrder.id,
			fetchData: async () => {
				await this.refreshData();
				if (this.patientFilterValue) this.onPatientChange(this.patientFilterValue);
			}
		});
	}

	@action.bound
	openFacilityOrderNewFlowExtraSuppliesAddModal() {
		this.facilityOrderNewFlowExtraSuppliesAddModal.open({
			facilityOrderId: this.facilityOrder.id,
			sendAgoraPo: this.facilityOrder.facility.sendAgoraPo,
			fetchData: async () => {
				await this.refreshData();
				if (this.patientFilterValue) this.onPatientChange(this.patientFilterValue);
			}
		});
	}

	@action.bound
	async sendAgoraPo() {
		this.sendingAgoraPo = true;
		try {
			await this.pickTicketHeaderService.sendAgoraPo(this.facilityOrder.pickTicketHeaderId);
			this.rootStore.notificationStore.success("Agora PO successfully sent.");
		}
		catch (err) {
			return err.data.errors == this.agoraPoErrorMessage ? this.rootStore.notificationStore.success(err.data.errors) : this.rootStore.notificationStore.error("Unable to send Agora PO!");
		}
		finally {
			this.sendingAgoraPo = false;
		}
	}

	@action.bound
	async openFacilityOrderNewFlowNonScheduledTubesModal(isSendOrder) {
		if (isSendOrder) {
			let params = {
				pageSize: 1,
				facilityOrderId: this.facilityOrder.id,
				notCurrentTube: true,
				hasIncludeNotCurrentTube: true,
				fields: "id",
				itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
			}
			const response = await this.facilityOrderTemplateService.find(params);
			const isNotCurrentTubeSaved = response.data.item && response.data.item.some(i => i);
			const isNotCurrentTubeAdded = (await this.facilityOrderService.get(this.facilityOrderId, { fields: "IsNotCurrentTubeAdded" })).data.isNotCurrentTubeAdded;
			if (!isNotCurrentTubeAdded && this.isNonScheduledItemsButtonVisible && (this.facilityOrder.facility.isMonthlyTrachTube || isNotCurrentTubeSaved)) {
				return this.facilityOrderNewFlowNonScheduledTubesModal.open({ facilityOrderId: this.facilityOrder.id, sendOrder: async () => await this.sendOrderNewFlow(), fetchData: () => this.queryUtility.fetch(), disabledSendOrder: this.disabledSendOrder });
			}
			await this.sendOrderNewFlow();
		}
		else {
			this.facilityOrderNewFlowNonScheduledTubesModal.open({
				facilityOrderId: this.facilityOrder.id,
				fetchData: async () => {
					await this.refreshData();
					if (this.patientFilterValue) this.onPatientChange(this.patientFilterValue);
				},
				disabledSendOrder: this.disabledSendOrder
			});
		}
	}

	@action.bound
	onOpenPumps() {
		window.open(
			`${this.rootStore.routerStore.findRoute('master.app.main.pumps.landing').pattern}?facilityId=${this.facilityOrder.facility.id}`
		)
	}

	@action.bound
	async downloadReport(name) {
		if (!this.facilityOrder.pickTicketHeaderId) {
			this.facilityOrder.pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrderId, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
			if (!this.facilityOrder.pickTicketHeaderId) {
				try {
					await this.pickTicketHeaderService.createPickTicketNewFlow({ facilityOrderId: this.facilityOrder.id, setSendOrder: false });
					this.rootStore.notificationStore.success("PICK_TICKET.CREATE.SUCCESS");
				}
				catch (err) {
					return this.rootStore.notificationStore.error(err.data ? err.data : "PICK_TICKET.CREATE.ERROR");
				}
			}
			await this.initializePickTicketHeader();
		}

		if (name === 'XLSX') {
			this.downloadPickTicket('XLSX');
		} else if (name === 'PDF') {
			this.downloadPickTicket('PDF');
		}
	}

	@action.bound
	async hasPrimaryRecipient() {
		return (await this.facilityOrderService.hasPrimaryRecipient(this.facilityOrderId)).data;
	}

	@action.bound
	async sendOrders(name) {
		if (name === "Send Packing List") {
			this.facilityOrderService.sendPackingListEmail();
			return;
		}

		let res = (await this.facilityOrderTemplateService.importCheck(this.facilityOrder.id, name)).data;
		if (!res.success) {
			return this.rootStore.notificationStore.error(res.errors);
		}
		if (name === 'Send Pick Ticket') {
			const recipientData = await this.hasPrimaryRecipient();
			const hasPickTicketRecipient = recipientData.pickTicketRecipientType;
			const hasPrimaryDeliveryRecipient = recipientData.deliveryTicketType;

			if (!hasPickTicketRecipient || !hasPrimaryDeliveryRecipient) {
				const missingName = [!hasPickTicketRecipient && "Packing List", !hasPrimaryDeliveryRecipient && "Delivery Ticket"].filter(Boolean).join(" and ");
				this.openPrimaryRecipientErrorModal(this.sendOrdersExtension, missingName);
				return;
			}
		}

		await this.sendOrdersExtension(name);
	}

	@action.bound
	async sendOrdersExtension(name) {
		if (!this.facilityOrder.pickTicketHeaderId && name !== 'Import to BT') {
			this.facilityOrder.pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrderId, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
			if (!this.facilityOrder.pickTicketHeaderId) {
				try {
					await this.pickTicketHeaderService.createPickTicketNewFlow({ facilityOrderId: this.facilityOrder.id, setSendOrder: true });
					this.rootStore.notificationStore.success("PICK_TICKET.CREATE.SUCCESS");
				}
				catch (err) {
					if (err.data == this.pickTicketErrorMessage) {
						return this.rootStore.notificationStore.success(name === 'Send Pick Ticket' ? err.data : this.agoraPoErrorMessage)
					}
					else {
						return this.rootStore.notificationStore.error(err.data ? err.data : "PICK_TICKET.CREATE.ERROR");
					}
				}
			}
			await this.initializePickTicketHeader();
		}

		if (name === 'Send Pick Ticket') {
			await this.openFacilityOrderNewFlowNonScheduledTubesModal(true);
		} else if (name === 'Send Agora PO') {
			await this.sendAgoraPo();
		} else if (name === 'Import to BT') {
			await this.showImportConfirmModal();
		}
	}

	@action.bound
	async downloadPickTicket(format) {
		if (this.facilityOrder.pickTicketHeaderId) {
			try {
				const pickTicketHeader = (await this.pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
				if (pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv === 'new') {
					await this.pickTicketHeaderService.refreshNewFlow(this.facilityOrder.pickTicketHeaderId);
				}
				const pickTicketReportDownloadUrl = `${this.pickTicketHeaderService.pickTicketReportOrderSummaryDownloadUrlNewFlow(this.facilityOrder.id)}?format=${format}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
				window.open(pickTicketReportDownloadUrl);
			}
			catch (err) {
				this.rootStore.notificationStore.error(err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH', err);
			}
		}
	}

	@action.bound
	async openRoundItemsModal(group) {
		await this.initializeFacilityOrder();
		let inventoryStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "inventory");
		if (this.isInventoryStepAllowed && (!(this.facilityOrder.missingInventoryCount || (inventoryStatus && inventoryStatus.completedOn)))) {
			this.rootStore.notificationStore.error("Rounding can only be done once the inventory is completed or skipped.");
			this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}

		let isRoundDown = group ? this.isRoundDown(group.orderQuantityUnitOfMeasures, group.qtyPerCase) : null;

		if (group && isRoundDown && group.facilityOrderTemplateItemOrderSummaryDetails && !group.facilityOrderTemplateItemOrderSummaryDetails.some(x => !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" || x.clinicalStatus == "Covered" || x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) && x.orderQuantity > 0 && x.orderQuantity >= x.sendSeparatelyQty)) {
			this.rootStore.notificationStore.error("Rounding down is not allowed because cutbacks cannot be applied to eligible patients.");
			this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}

		let items = [];
		if (!group) {
			this.facilityOrderTemplateItemsGroupedByItemCategory.forEach(category => {
				category.items.forEach(item => {
					if (item.errorMessage != "Rounding is not allowed for this item because it can only be partially rounded." && this.isRoundDown(item.orderQuantityUnitOfMeasures, item.qtyPerCase) && item.showRoundOption && item.patientCount < 4 && item.facilityOrderTemplateItemOrderSummaryDetails.some(x => !(x.eligibilityStatus == "Eligible" && (x.clinicalStatus == "Prior Auth Obtained" || x.clinicalStatus == "Prior Auth Required" || x.clinicalStatus == "Prior Auth Pending" || x.clinicalStatus == "Covered" || x.clinicalStatus == "Does Not Meet Insurance Criteria" || x.clinicalStatus == null)) && x.orderQuantity > 0 && x.orderQuantity >= x.sendSeparatelyQty)) {
						items.push(item);
					}
				});
			});
		}

		if (group && group.errorMessage != "Rounding is not allowed for this item because it can only be partially rounded." && isRoundDown && group.patientCount < 4) {
			this.facilityOrderNewFlowRoundingItemsModal.open({
				roundItems: async () => await this.roundToNearestCase(group)
			});
		}
		else if (items && items.length > 0) {
			this.facilityOrderNewFlowRoundingItemsModal.open({
				roundItems: async () => await this.roundToNearestCase(group),
				items: items
			});
		}
		else if (group && group.errorMessage == "Rounding is not allowed for this item because it can only be partially rounded." && isRoundDown) {
			this.rootStore.notificationStore.error("Rounding is not allowed for this item because it can only be partially rounded.");
			this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
			this.savingResource = false;
			this.disabledItemButtons = false;
			return;
		}
		else {
			await this.roundToNearestCase(group);
		}
	}

	isRoundDown(orderQuantityString, qty) {
		const parts = orderQuantityString.split(",");
		if (parts.length < 2)
			return false;

		const secondPart = parts[1].trim();
		const match = secondPart.match(/^(\d+)/);

		if (!match)
			return false;

		const secondNumber = parseInt(match[1], 10);

		return secondNumber < qty / 2;
	}

	@action.bound
	async roundToNearestCase(group) {
		this.savingResource = true;
		this.isEditDisabled = true;
		this.disabledItemButtons = true;
		try {
			var response = await this.facilityOrderTemplateItemService.roundToTheNearestCase({ facilityOrderId: this.facilityOrderId, productId: group ? group.productId : null, facilityOrderTemplateItemIds: group ? [group.productId] : _.flatMap(this.facilityOrderTemplateItemsGroupedByItemCategory, category => _.filter(category.items, item => item.includeItem)).map(x => x.productId) });
			if (response && response.data && response.data.errors && response.data.errors[0]) {
				this.rootStore.notificationStore.error(response.data.errors[0]);
				this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
				this.savingResource = false;
				this.disabledItemButtons = false;
				return;
			}
			else {
				this.rootStore.notificationStore.success(group ? "Item successfully rounded." : "All items that were able to be rounded up or down to the nearest case have been adjusted.");
			}
		}
		catch (err) {
			this.rootStore.notificationStore.error("An error occured while rounding.");
		}

		this.isEditDisabled = (this.pickTicketHeaderStatus && this.pickTicketHeaderStatus != "new") || (this.facilityOrder && this.facilityOrder.sendOrder);
		this.savingResource = false;
		this.disabledItemButtons = false;
		await this.refreshData();

		if (this.patientFilterValue)
			this.onPatientChange(this.patientFilterValue);

	}

	@action.bound
	recalculateOrderQuantity(group) {
		let filteredItems = group.facilityOrderTemplateItemOrderSummaryDetails.filter(x => (x.inactiveReasonId == 0 || x.inactiveReasonId == null) && !(x.notCurrentTube && x.orderQuantity > 0));
		let SOQtySum = _.sumBy(filteredItems, i => parseInt(i.orderQuantity ? i.orderQuantity : 0));
		let orderQuantityUnitOfMeasures = group.qtyPerCase == 0 ? `0 CS, ${SOQtySum} EA` : `${Math.floor(SOQtySum / group.qtyPerCase)} CS, ${SOQtySum % group.qtyPerCase} EA`;
		group.orderQuantityUnitOfMeasures = group.qtyPerCase == 0 || group.qtyPerCase == null || orderQuantityUnitOfMeasures == 0 ? "" : orderQuantityUnitOfMeasures;
		group.orderQuantitySum = SOQtySum;
		group.showRoundOption = group.itemCategory.toLowerCase() == "enteral" && group.hasCaseVersion && group.qtyPerCase != 0 && group.procedureCode != "B4153" && !group.orderQuantityUnitOfMeasures.includes(", 0 EA") && group.orderQuantitySum != 0;
	}

	@action.bound
	setSsQtyInputRef(el) {
		this.ssQtyInputRef = el;
	}

	@action.bound
	getSsQtyInputValue() {
		return this.ssQtyInputRef ? this.ssQtyInputRef.value : '';
	}

	@action.bound
	async refreshData(message) {
		this.setAnyDNS();

		let params = {
			...this.queryUtility.plainFilter,
			pageSize: 10000,
			facilityOrderId: this.facilityOrderId,
		};

		if (this.facilityOrder.isEnteralWeek) {
			params = _.extend(params, {
				itemCategoryId: this.enteralItemCategoryId
			});
		}

		const response = await this.facilityOrderTemplateItemService.getOrderSummaryItems(params);
		this.originalData = (response && response.data && response.data.item) ? response.data.item : null;

		if (!this.originalData) {
			return;
		}

		let foundDNS = false;
		let facilityOrderTemplateItemsGroupedByItemCategoryTemp = [];

		const patient = this.localPatientStore.items.find(x => x.name === this.patientName);
		const item = this.itemStore.find(x => x.itemId === this.itemName || x.itemName === this.itemName);
		let filteredItems = this.originalData.filter(i =>
			i.facilityOrderTemplateItemOrderSummaryDetails.some(d => (patient ? d.patientId === patient.patientId : d.patient.toLowerCase().startsWith(this.patientName.toLowerCase()))) &&
			(item ? i.itemId === item.itemId : (i.itemId.toLowerCase().includes(this.itemName.toLowerCase()) || i.itemName.toLowerCase().includes(this.itemName.toLowerCase()))));

		let grouped = _.groupBy(filteredItems, 'itemCategory');

		_.each(grouped, (val, key) => {
			facilityOrderTemplateItemsGroupedByItemCategoryTemp.push({
				id: key,
				category: val[0].itemCategory,
				items: _.map(val, (i) => {
					if (i.facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId == 16 || this.isNonScheduled(x))) {
						foundDNS = true;
					}

					let currentItem = this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key) && this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key).items.find(x => x.itemId == i.itemId);

					const locallyChanged =
						currentItem &&
						(
							i.orderQuantitySum !== currentItem.orderQuantitySum ||
							i.sendSeparatelyQuantitySum !== currentItem.sendSeparatelyQuantitySum ||
							i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length
						);
					let refreshed = !currentItem || (currentItem && message && message.table == "FOT" && i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length) || (message && i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.some(x => message.table == "FOTI" ? (x.facilityOrderTemplateItemId == message.customId || ((i.facilityOrderTemplateItemOrderSummaryDetails && i.facilityOrderTemplateItemOrderSummaryDetails.length != currentItem.facilityOrderTemplateItemOrderSummaryDetails.length) && currentItem.itemId == i.itemId)) : x.facilityOrderPatientId == message.id || x.facilityOrderTemplateId == message.id)) || (!message && locallyChanged);
					return {
						...i,
						isRefreshed: (currentItem && (currentItem.isEditMode || currentItem.isEditSendSeparatelyMode) && refreshed) || (currentItem && currentItem.isRefreshed),
						showDNSItems: currentItem && currentItem.showDNSItems,
						show: !(currentItem && (currentItem.isEditMode || currentItem.isEditSendSeparatelyMode) && refreshed) && currentItem && currentItem.show,
						hasDNSItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => x.inactiveReasonId === 16),
						isEditMode: !refreshed && currentItem && currentItem.isEditMode,
						isEditSendSeparatelyMode: currentItem && currentItem.isEditSendSeparatelyMode && !refreshed,
						showNonScheduledItems: currentItem && currentItem.showNonScheduledItems,
						hasNonScheduledItems: i.facilityOrderTemplateItemOrderSummaryDetails.some(x => this.isNonScheduled(x)),
						showRoundOption:
							val[0].itemCategory.toLowerCase() === "enteral" &&
							i.hasCaseVersion &&
							i.qtyPerCase !== 0 &&
							i.procedureCode !== "B4153" &&
							!i.orderQuantityUnitOfMeasures.includes(", 0 EA") &&
							i.orderQuantitySum !== 0,
						includeItem: false
					};
				}),
				show: this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key) && this.facilityOrderTemplateItemsGroupedByItemCategory.find(x => x.id == key).show
			});
		});
		if (foundDNS) {
			this.setAnyDNS.cancel();
			this.anyDNS = true;
		}

		this.filterItemStore();
		await this.localPatientStore.filterAsync();

		this.facilityOrderTemplateItemsGroupedByItemCategory = facilityOrderTemplateItemsGroupedByItemCategoryTemp;
		this.facilityOrderTemplateItemsGroupedByItemCategoryEditMode = facilityOrderTemplateItemsGroupedByItemCategoryTemp;

		this.rebuildTooltip = true;
		this.rebuildTooltip = false;
	}

	@action.bound
	filterItemStore() {
		this.itemStore = this.originalData.map(i => ({
			itemId: i.itemId,
			itemName: i.itemName,
			description: i.itemName
		}));
	}
}

export default FacilityOrderNewFlowOrderSummaryViewStore;